import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Table,
  Badge,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Modal,
  Dropdown
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import { jwtDecode } from "jwt-decode";
import { Image } from 'react-bootstrap';
import { CiCalendar } from "react-icons/ci";
import searchIcon from "../assets/images/icons/search.png";
import creditManagementIcon from "../assets/images/icons/credit-red-management-icon.png";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import "react-datepicker/dist/react-datepicker.css";
import { FaEllipsisV } from "react-icons/fa";
import { GrEdit } from "react-icons/gr";
import { RiDeleteBin5Line } from "react-icons/ri";
import { IoFilterOutline } from "react-icons/io5";
import { FcClearFilters } from "react-icons/fc";
import { creditService } from '../services/creditService';
import { accountService } from '../services/accountService';
import { userService } from '../services/userService';

const CreditRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [token, setToken] = useState(() => localStorage.getItem("token") || "");
  const [accountId, setAccountId] = useState("");
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [username, setUsername] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [creditTypeFilter, setCreditTypeFilter] = useState("");

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      try {
        const decoded = jwtDecode(storedToken);
        const id = decoded.companyId || decoded.accountId;
        setAccountId(id);
        setUserRole(decoded.roles[0]);
        // Capture username for approve/reject APIs
        setUsername(decoded.sub || decoded.username || '');
      } catch (error) {
        console.error("Error decoding token:", error);
        toast.error("Invalid token, please login again");
      }
    }
  }, []);

  const fetchCreditRequests = async () => {
    try {
      setError(null);
      setNotFound(false);
      setLoading(true);

      const formattedStartDate = startDate ? startDate.toISOString().split('T')[0] : '';
      const formattedEndDate = endDate ? endDate.toISOString().split('T')[0] : '';

      // For ADMIN role, use the same logic as USER but ensure we have the correct accountId
      const accountIdToUse = userRole === 'SUPERADMIN' ? null : accountId;
      

      // Convert 0-based pagination to 1-based for API
      const apiPage = currentPage ;
      
      
      // Map UI filter to server status (omit for ALL)
      const serverStatus = filterType === 'ALL' ? undefined : filterType;

      const extraParams =
        userRole === 'SUPERADMIN'
          ? {
              search: '',
              status: serverStatus,
              creditType: creditTypeFilter,
              sortBy: 'requestedAt',
              sortDirection: 'DESC',
            }
          : {
              status: serverStatus,
              creditType: creditTypeFilter,
            };

      const response = await creditService.getCreditRequests(
        accountIdToUse,
        apiPage,
        10,
        formattedStartDate,
        formattedEndDate,
        userRole,
        extraParams
      );


      if (response) {
        // Service normalizes response to data object
        const requests = response.content || [];
        setRequests(requests);
        setTotalPages(response.totalPages || 0);
        setTotalRecords(
          response.totalRecords || response.totalElements || requests.length || 0
        );
      } else {
        setRequests([]);
        setTotalPages(0);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching credit requests:", error);
      if (error.response && error.response.status === 404) {
        // Show Not Found state in UI without error toast
        setNotFound(true);
        setRequests([]);
        setTotalPages(0);
        setTotalRecords(0);
      } else {
        setError(error.message || "Failed to load credit requests");
        toast.error(error.response?.data?.message || "Failed to load credit requests");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 'SUPERADMIN' || accountId) {
      fetchCreditRequests();
    }
  }, [accountId, currentPage, startDate, endDate, userRole, filterType, creditTypeFilter]);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'PROCESSING':
        return 'info';
      default:
        return 'danger';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).replace(',', ' |');
  };

  const handleApprove = async (requestId) => {
    try {
      const response = await creditService.approveCreditRequest(requestId, username);

      if (response.success) {
        setRequests(prevRequests => 
          prevRequests.map(request => 
            request.id === requestId
              ? {
                  ...request,
                  status: 'APPROVED',
                  approvedBy: username,
                  approvedAt: new Date().toISOString()
                }
              : request
          )
        );
        toast.success("Credit request approved successfully");
      }
    } catch (error) {
      console.error("Error approving request:", error);
      toast.error(error.response?.data?.message || "Failed to approve request");
    }
  };

  const handleReject = async () => {
    try {
      const response = await creditService.rejectCreditRequest(selectedRequestId, rejectRemarks, username);

      if (response.success) {
        setRequests(prevRequests => 
          prevRequests.map(request => 
            request.id === selectedRequestId
              ? {
                  ...request,
                  status: 'REJECTED',
                  remarks: rejectRemarks,
                  approvedBy: username,
                  approvedAt: new Date().toISOString()
                }
              : request
          )
        );
        toast.success("Credit request rejected successfully");
        setShowRejectModal(false);
        setRejectRemarks('');
        setSelectedRequestId(null);
      }
    } catch (error) {
      console.error("Error rejecting request:", error);
      toast.error(error.response?.data?.message || "Failed to reject request");
    }
  };

  const handleFilterChange = (filterType) => {
    setFilterType(filterType);
    setCurrentPage(0);
  };

  const handleCreditTypeChange = (type) => {
    setCreditTypeFilter(type);
    setCurrentPage(0);
  };

  if (error) {
    return (
      <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden bg-white">
        <div className="text-center py-4">
          <p className="text-danger">Error: {error}</p>
          <Button variant="primary" onClick={fetchCreditRequests}>
            Retry
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="ps-0 pb-4 pe-0 overflow-hidden bg-white">
      <ToastContainer position="top-right" autoClose={3000} />
      <Card className="card-nostyle" style={{marginTop:'70px'}}>
        <Card.Body className="ps-0 pe-0">
          <div className="custom-domain-containers">
            <div
              className="d-flex justify-content-between ps-4 pe-4 align-items-center"
              style={{ borderBottom: "1px solid #EAECF0" }}
            >
              <h4 className="mb-0 bg-white br-radius-12 rounded-bottom-0 pb-3 fs-20 poppins fw-600">
                <Image src={creditManagementIcon} className="me-1" width="20" />{" "}
                Credit Requests
              </h4>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="card-nostyle card ps-2 pe-2">
        <Card.Body className="pt-2">
          <div className="d-block d-md-flex justify-content-between align-items-center mb-4">
            <div className="d-block d-md-flex align-items-center gap-3">
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="bg-white cus-dropdown-toggle-show"
                  style={{ borderRadius: "8px", borderColor: "#C2C2C2" }}
                >
                  <IoFilterOutline className="me-2" />
                  {filterType === "ALL"
                    ? "All Requests"
                    : filterType === "APPROVED"
                    ? "Approved Requests"
                    : filterType === "PENDING"
                    ? "Pending Requests"
                    : filterType === "PROCESSING"
                    ? "Processing Requests"
                    : filterType === "REJECTED"
                    ? "Rejected Requests"
                    : "Filter"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item 
                    active={filterType === "ALL"}
                    onClick={() => handleFilterChange("ALL")}
                  >
                    All Requests
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={filterType === "APPROVED"}
                    onClick={() => handleFilterChange("APPROVED")}
                  >
                    Approved Requests
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={filterType === "PENDING"}
                    onClick={() => handleFilterChange("PENDING")}
                  >
                    Pending Requests
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={filterType === "PROCESSING"}
                    onClick={() => handleFilterChange("PROCESSING")}
                  >
                    Processing Requests
                  </Dropdown.Item>
                  <Dropdown.Item 
                    active={filterType === "REJECTED"}
                    onClick={() => handleFilterChange("REJECTED")}
                  >
                    Rejected Requests
                  </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                className="bg-white cus-dropdown-toggle-show"
                style={{ borderRadius: "8px", borderColor: "#C2C2C2" }}
              >
                Credit Type: {creditTypeFilter === 'SHORTENING' ? 'Shortening' :creditTypeFilter === 'TRACKING' ? 'Tracking' : 'No Filter'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  active={creditTypeFilter === 'SHORTENING'}
                  onClick={() => handleCreditTypeChange('SHORTENING')}
                >
                  Shortening
                </Dropdown.Item>
                <Dropdown.Item
                  active={creditTypeFilter === 'TRACKING'}
                  onClick={() => handleCreditTypeChange('TRACKING')}
                >
                  Tracking
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

              <div
                className="d-flex align-items-center mt-2 mt-md-0 border overflow-hidden"
                style={{ borderRadius: "8px", width: "250px" }}
              >
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                    setCurrentPage(0);
                  }}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  className="border-0 form-control"
                />
                <span className="mx-2">⇆</span>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => {
                    setEndDate(date);
                    setCurrentPage(0);
                  }}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  className="border-0 form-control"
                />
                <CiCalendar
                  style={{ fontSize: "36px" }}
                  className="ms-2 me-2"
                />
              </div>

              {(filterType !== "ALL" || creditTypeFilter !== "") && (
                <Button
                  variant="light"
                  size="sm"
                  className="d-flex align-items-center gap-2 mt-2 mt-md-0"
                  style={{
                    border: "1px solid #EAECEE",
                    borderRadius: "8px",
                    padding: "9px 15px",
                  }}
                  onClick={() => {
                    setFilterType("ALL");
                    setCreditTypeFilter("");
                    setCurrentPage(0);
                    const defaultStart = new Date();
                    defaultStart.setMonth(defaultStart.getMonth() - 1);
                    setStartDate(defaultStart);
                    setEndDate(new Date());
                  }}
                >
                  <FcClearFilters size={12} />
                  <span className="fw-400 fs-12">Clear All</span>
                </Button>
              )}
            </div>
          </div>

          {/* Summary */}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              <style>{`
                .table-responsive {
                  overflow-x: auto !important;
                }
              `}</style>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th>Requested At</th>
                    <th>Requested Credits</th>
                    <th>Requested By</th>
                    <th>Credit Type</th>
                    <th>Status</th>
                    <th>Remarks</th>
                    {userRole === 'SUPERADMIN' && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {requests
                    .filter(request => {
                      switch (filterType) {
                        case "APPROVED":
                          return request.status === "APPROVED";
                        case "PENDING":
                          return request.status === "PENDING";
                        case "PROCESSING":
                          return request.status === "PROCESSING";
                        case "REJECTED":
                          return request.status === "REJECTED";
                        default:
                          return true;
                      }
                    })
                    .map((request) => (
                      <tr key={request.id}>
                        <td>{formatDate(request.requestedAt)}</td>
                        <td>{request.requestedCredits}</td>
                        <td>{request.requestedBy}</td>
                        <td>{request.creditType || 'SHORTENING'}</td>
                        <td>
                          <Badge bg={getStatusBadgeColor(request.status)}>
                            {request.status}
                          </Badge>
                        </td>
                        <td>{request.remarks}</td>
                        {userRole === 'SUPERADMIN' && (
                          <td>
                            {request.status === 'PENDING' && (
                              <Dropdown className="cust-drop">
                                <Dropdown.Toggle variant="link" id="dropdown-basic">
                                  <FaEllipsisV className="text-A8B0B9 fs-12" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    href="#"
                                    onClick={() => handleApprove(request.id)}
                                    className="fs-12 pt-2 pb-2"
                                  >
                                    <GrEdit className="align-middle fs-12 me-1" /> Approve
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className="fs-12 pt-2 pb-2"
                                    href="#"
                                    onClick={() => {
                                      setSelectedRequestId(request.id);
                                      setShowRejectModal(true);
                                    }}
                                  >
                                    <RiDeleteBin5Line className="align-middle fs-14 me-1" /> Reject
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                            {request.status !== 'PENDING' && (
                              <span className="text-muted">Action Performed</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </Table>

              {requests.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">{notFound ? 'Not found' : 'No credit requests found'}</p>
                </div>
              )}
                
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-3">
                  <nav>
                    <ul className="pagination mb-2">
                      <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(0)}
                          disabled={currentPage === 0}
                        >
                          &laquo;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 0}
                        >
                          &lt;
                        </button>
                      </li>
                      {(() => {
                        let startPage = Math.max(currentPage - 1, 0);
                        let endPage = Math.min(startPage + 2, totalPages - 1);
                        
                        if (endPage === totalPages - 1) {
                          startPage = Math.max(endPage - 2, 0);
                        }

                        return [...Array(endPage - startPage + 1)].map((_, idx) => (
                          <li
                            key={startPage + idx}
                            className={`page-item ${currentPage === startPage + idx ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(startPage + idx)}
                            >
                              {startPage + idx + 1}
                            </button>
                          </li>
                        ));
                      })()}
                      <li className={`page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages - 1}
                        >
                          &gt;
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(totalPages - 1)}
                          disabled={currentPage === totalPages - 1}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showRejectModal}
        onHide={() => {
          setShowRejectModal(false);
          setRejectRemarks('');
          setSelectedRequestId(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reject Credit Request</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Rejection Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              placeholder="Enter rejection remarks"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setRejectRemarks('');
              setSelectedRequestId(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!rejectRemarks.trim()}
          >
            Reject Request
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CreditRequests;